import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { kioskAuthService, KioskSession } from '../services/auth.service';
import { setKioskSessionToken } from '../services/enrollment.service';

/**
 * Why the kiosk has no usable session. Kept separate from the human-readable message so the
 * UI can translate it AND give the right advice: a new enrollment session fixes a bad token,
 * but only waiting (or IT) fixes an unreachable backend.
 */
export type KioskAuthErrorCode =
  | 'missingToken'
  | 'invalidSession'
  | 'sessionExpired'
  | 'serverUnreachable'
  | 'unknown';

export interface KioskAuthContextValue {
  session: KioskSession | null;
  sessionToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  errorCode: KioskAuthErrorCode | null;
  expiresAt: number | null;
  retry: () => void;
}

/** Refresh this long before expiry, and back off this long when the backend is unreachable. */
const REFRESH_LEAD_MS = 5 * 60 * 1000;
const REFRESH_RETRY_MS = 30 * 1000;

const ERROR_MESSAGES: Record<KioskAuthErrorCode, string> = {
  missingToken: 'No session token provided. Please open this page from the CMS.',
  invalidSession:
    'Invalid session. Please close this window and start a new enrollment session.',
  sessionExpired:
    'Session expired. Please close this window and start a new enrollment session.',
  serverUnreachable:
    'Cannot reach the enrollment server. Please check the connection and try again.',
  unknown: 'Failed to authenticate. Please try again.',
};

const KioskAuthContext = createContext<KioskAuthContextValue>({
  session: null,
  sessionToken: null,
  isLoading: true,
  isAuthenticated: false,
  error: null,
  errorCode: null,
  expiresAt: null,
  retry: () => {},
});

export function useKioskAuth() {
  return useContext(KioskAuthContext);
}

interface KioskAuthProviderProps {
  children: React.ReactNode;
}

export function KioskAuthProvider({ children }: KioskAuthProviderProps) {
  const [session, setSession] = useState<KioskSession | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<KioskAuthErrorCode | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);

  const refreshTimerRef = useRef<number | null>(null);
  // The token is stripped from the URL immediately, so a reload cannot recover it.
  // Keep it here so retry() can re-validate after a transient outage.
  const initialTokenRef = useRef<string | null>(null);

  // Clean URL by removing session parameter
  const cleanUrl = useCallback(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has('session')) {
      url.searchParams.delete('session');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  const failWith = useCallback((code: KioskAuthErrorCode) => {
    setErrorCode(code);
    setError(ERROR_MESSAGES[code]);
    setSession(null);
    setSessionToken(null);
    setKioskSessionToken(null);
  }, []);

  // Schedule session refresh 5 minutes before expiry
  const scheduleRefresh = useCallback(
    (token: string, expiry: number) => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }

      // A session that is already inside the lead window should refresh right away
      // rather than never being refreshed at all.
      const refreshIn = Math.max(0, expiry - Date.now() - REFRESH_LEAD_MS);

      refreshTimerRef.current = window.setTimeout(async () => {
        console.log('Refreshing kiosk session...');
        const result = await kioskAuthService.refreshSession(token);

        if (result.status === 'valid') {
          setSessionToken(result.sessionToken);
          setExpiresAt(result.expiresAt);
          setKioskSessionToken(result.sessionToken);
          scheduleRefresh(result.sessionToken, result.expiresAt);
          return;
        }

        if (result.status === 'unreachable' && Date.now() < expiry) {
          // The backend blipped but the session is still valid - do NOT discard the
          // half-filled enrollment form. Try again shortly, while time remains.
          console.warn('Kiosk session refresh unreachable, retrying shortly');
          if (refreshTimerRef.current) {
            clearTimeout(refreshTimerRef.current);
          }
          refreshTimerRef.current = window.setTimeout(
            () => scheduleRefresh(token, expiry),
            Math.min(REFRESH_RETRY_MS, Math.max(0, expiry - Date.now())),
          );
          return;
        }

        failWith('sessionExpired');
      }, refreshIn);
    },
    [failWith],
  );

  const validateAndApply = useCallback(
    async (token: string) => {
      setIsLoading(true);
      setError(null);
      setErrorCode(null);

      try {
        const outcome = await kioskAuthService.validateSession(token);

        if (outcome.status === 'valid') {
          const { session: validSession } = outcome;
          setSession(validSession);
          setSessionToken(token);
          setExpiresAt(validSession.expiresAt);
          setKioskSessionToken(token);
          scheduleRefresh(token, validSession.expiresAt);
          console.log(
            `Kiosk session authenticated: ${validSession.sessionId} (${validSession.staffName})`,
          );
          return;
        }

        failWith(outcome.status === 'unreachable' ? 'serverUnreachable' : 'invalidSession');
      } catch (err) {
        console.error('Failed to initialize kiosk auth:', err);
        failWith('unknown');
      } finally {
        setIsLoading(false);
      }
    },
    [failWith, scheduleRefresh],
  );

  /** Re-validate the token we started with. Used after a transient backend outage. */
  const retry = useCallback(() => {
    const token = initialTokenRef.current;
    if (!token) {
      return;
    }
    void validateAndApply(token);
  }, [validateAndApply]);

  // Initialize auth from URL on mount
  useEffect(() => {
    // Get session token from URL
    const token = new URL(window.location.href).searchParams.get('session');
    initialTokenRef.current = token;

    // Clean URL immediately to prevent token exposure
    cleanUrl();

    if (!token) {
      failWith('missingToken');
      setIsLoading(false);
    } else {
      void validateAndApply(token);
    }

    // Cleanup on unmount
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [cleanUrl, failWith, validateAndApply]);

  const value: KioskAuthContextValue = {
    session,
    sessionToken,
    isLoading,
    isAuthenticated: !!session,
    error,
    errorCode,
    expiresAt,
    retry,
  };

  return (
    <KioskAuthContext.Provider value={value}>
      {children}
    </KioskAuthContext.Provider>
  );
}
